import { Injectable, inject, signal, computed } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom, timeout } from 'rxjs';
import { AuthApiService } from '@core/auth/auth-api.service';
import { hasHttpStatus, isTransientHttpError } from '@core/http/http-status.util';
import { UserPreferencesStore } from '@core/user-preferences';
import { AuthSessionStateService } from './auth-session-state.service';

type SessionStatus = 'unchecked' | 'ready' | 'unavailable';

/** Backoff du bootstrap : 500 ms puis 1,5 s avant de déclarer le service indisponible. */
const BOOTSTRAP_RETRY_DELAYS_MS = [500, 1500];
const SESSION_REQUEST_TIMEOUT_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly router = inject(Router);
  private readonly authApi = inject(AuthApiService);
  private readonly preferencesStore = inject(UserPreferencesStore);
  private readonly state = inject(AuthSessionStateService);
  private readonly status = signal<SessionStatus>('unchecked');
  private readonly checking = signal(false);
  private inflight: Promise<void> | null = null;
  private waiters: (() => void)[] = [];

  readonly isAuthenticated = computed(() => this.state.session() !== null);
  readonly userId = computed(() => this.state.session()?.userId ?? null);
  readonly roles = computed(() => this.state.session()?.roles ?? []);
  readonly username = computed(() => this.state.session()?.username ?? null);
  /** Verdict rendu : authentifié ou anonyme (401). Faux tant que le service est indisponible. */
  readonly sessionChecked = computed(() => this.status() === 'ready');
  /** Le serveur n'a pas pu trancher : on ne redirige jamais vers /login dans cet état. */
  readonly sessionUnavailable = computed(() => this.status() === 'unavailable');
  readonly sessionChecking = this.checking.asReadonly();
  readonly isAdmin = computed(() => this.roles().includes('ADMIN'));

  /** Résolu dès qu'un verdict (authentifié ou 401) est connu ; reste en attente si indisponible. */
  whenVerdict(): Promise<void> {
    if (this.status() === 'ready') return Promise.resolve();
    return new Promise(resolve => this.waiters.push(resolve));
  }

  /** Seul un 401 déconnecte : toute autre erreur laisse l'état de session intact. */
  checkSession(): Promise<void> {
    this.inflight ??= this.runCheck().finally(() => {
      this.inflight = null;
      this.checking.set(false);
    });
    return this.inflight;
  }

  /** Bouton « Réessayer » : nouvelle vérification complète, puis préférences si connecté. */
  async retrySession(): Promise<void> {
    await this.checkSession();
    if (this.isAuthenticated()) await this.preferencesStore.loadPreferences();
  }

  async onAuthSuccess(): Promise<void> {
    await this.checkSession();
    if (this.isAuthenticated()) await this.preferencesStore.loadPreferences(true);
  }

  async logout(): Promise<void> {
    this.state.invalidate();
    try {
      await firstValueFrom(this.authApi.logout());
    } catch {
      /* L'état local est déjà invalidé avant l'envoi. */
    }
    await this.router.navigate(['/login']);
  }

  clearState(): void {
    this.state.invalidate();
  }

  private async runCheck(): Promise<void> {
    this.checking.set(true);
    const generation = this.state.generation;
    // Une session déjà connue n'est pas rejouée : une seule sonde, l'état est préservé.
    const delays = this.isAuthenticated() ? [] : BOOTSTRAP_RETRY_DELAYS_MS;
    for (let attempt = 0; ; attempt++) {
      try {
        const session = await firstValueFrom(
          this.authApi.getMe().pipe(timeout(SESSION_REQUEST_TIMEOUT_MS))
        );
        this.state.accept(session, generation);
        return this.settle('ready');
      } catch (error: unknown) {
        if (hasHttpStatus(error, 401)) {
          if (generation === this.state.generation) this.clearIfAuthenticated();
          return this.settle('ready');
        }
        const delay = delays[attempt];
        if (delay === undefined || !isTransientHttpError(error)) {
          return this.settle(this.status() === 'ready' ? 'ready' : 'unavailable');
        }
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  private settle(status: SessionStatus): void {
    this.status.set(status);
    if (status !== 'ready') return;
    const released = this.waiters;
    this.waiters = [];
    released.forEach(release => release());
  }

  private clearIfAuthenticated(): void {
    if (this.isAuthenticated()) this.state.invalidate();
  }
}
